<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
        <i class="bi bi-arrow-left me-2"></i>
        Back to Organizations
      </button>
    </div>

    <div class="row">
      <div class="col-12 col-lg-10 col-xl-8">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-person-plus-fill me-2"></i>
              Create User & Organization
            </h5>
            <p class="text-muted small mb-0 mt-1">
              Streamlined workflow to set up a new user and organization together
            </p>
          </div>
          <div class="card-body">
            <!-- Error Message -->
            <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>

            <form [formGroup]="createForm" (ngSubmit)="onSubmit()">

              <!-- ==================== USER SECTION ==================== -->
              <div class="border-bottom pb-2 mb-3">
                <h6 class="fw-semibold mb-1">
                  <i class="bi bi-person me-2"></i>
                  User
                </h6>
                <p class="text-muted small mb-0">Create a new user or assign an existing one</p>
              </div>

              <!-- User Mode Toggle -->
              <div class="btn-group w-100 mb-3" role="group" aria-label="User mode">
                <input
                  type="radio"
                  class="btn-check"
                  name="userMode"
                  id="userModeNew"
                  autocomplete="off"
                  [checked]="userMode === 'new'"
                  (change)="setUserMode('new')"
                />
                <label class="btn btn-outline-primary" for="userModeNew">
                  <i class="bi bi-person-plus me-2"></i>
                  Create New User
                </label>

                <input
                  type="radio"
                  class="btn-check"
                  name="userMode"
                  id="userModeExisting"
                  autocomplete="off"
                  [checked]="userMode === 'existing'"
                  (change)="setUserMode('existing')"
                />
                <label class="btn btn-outline-primary" for="userModeExisting">
                  <i class="bi bi-search me-2"></i>
                  Assign Existing User
                </label>
              </div>

              <!-- New User Fields -->
              <div class="card border-0 mb-4">
                <div class="card-body">
                  <!-- Email (always shown) -->
                  <div class="row">
                    <div class="col-12 mb-3">
                      <label for="email" class="form-label">
                        Email Address <span class="text-danger">*</span>
                      </label>
                      <div class="input-group">
                        <input
                          type="email"
                          class="form-control"
                          id="email"
                          formControlName="email"
                          placeholder="user@example.com"
                          (input)="onEmailChange($event)"
                          [class.is-invalid]="createForm.get('email')?.invalid && createForm.get('email')?.touched && userMode !== 'existing'"
                        />
                        <button
                          *ngIf="userMode === 'existing'"
                          type="button"
                          class="btn btn-outline-secondary"
                          (click)="lookupUser()"
                          [disabled]="lookingUpUser || !createForm.get('email')?.value"
                        >
                          <span *ngIf="lookingUpUser" class="spinner-border spinner-border-sm me-1"></span>
                          <i *ngIf="!lookingUpUser" class="bi bi-search me-1"></i>
                          Lookup
                        </button>
                      </div>
                      <div *ngIf="createForm.get('email')?.invalid && createForm.get('email')?.touched" class="invalid-feedback d-block">
                        Valid email is required
                      </div>
                    </div>
                  </div>

                  <!-- Existing User Found -->
                  <div *ngIf="userMode === 'existing' && existingUser" class="alert alert-success mb-3">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <strong><i class="bi bi-check-circle me-2"></i>User Found</strong>
                        <div class="mt-1">
                          <div>{{ existingUser.firstName }} {{ existingUser.lastName }}</div>
                          <div class="text-muted small">{{ existingUser.email }}</div>
                          <div *ngIf="existingUser.organizations?.length" class="text-muted small mt-1">
                            Already in: {{ existingUser.organizations.length }} organization(s)
                          </div>
                        </div>
                      </div>
                        <!-- <button type="button" class="btn btn-sm btn-success" (click)="useExistingUser()">
                          Use This User
                        </button> -->
                    </div>
                  </div>

                  <!-- User Not Found (in existing mode) -->
                  <div *ngIf="userMode === 'existing' && !existingUser && !lookingUpUser && createForm.get('email')?.value" class="alert alert-warning mb-3">
                    <i class="bi bi-exclamation-triangle me-2"></i>
                    No user found with this email.
                    <button type="button" class="btn btn-sm btn-outline-primary ms-2" (click)="createNewUserInstead()">
                      Create New User Instead
                    </button>
                  </div>

                  <!-- Name fields (only for new users) -->
                  <div *ngIf="userMode === 'new'" class="row">
                    <div class="col-md-6 mb-3">
                      <label for="firstName" class="form-label">First Name</label>
                      <input
                        type="text"
                        class="form-control"
                        id="firstName"
                        formControlName="firstName"
                        placeholder="John"
                      />
                      <div class="form-text">Optional - can be added later</div>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label for="lastName" class="form-label">Last Name</label>
                      <input
                        type="text"
                        class="form-control"
                        id="lastName"
                        formControlName="lastName"
                        placeholder="Doe"
                      />
                      <div class="form-text">Optional - can be added later</div>
                    </div>
                  </div>

                  <!-- Role Selection -->
                  <div class="mb-0">
                    <label class="form-label">Role <span class="text-danger">*</span></label>
                    <div class="row">
                      <div *ngFor="let role of availableRoles" class="col-md-6 mb-2">
                        <div
                          class="card role-card h-100 cursor-pointer"
                          [class.selected]="isRoleSelected(role.value)"
                          (click)="setRole(role.value)"
                        >
                          <div class="card-body py-2 px-3">
                            <div class="d-flex align-items-center">
                              <input
                                type="radio"
                                class="form-check-input me-2"
                                [checked]="isRoleSelected(role.value)"
                                (click)="$event.stopPropagation()"
                                (change)="setRole(role.value)"
                              />
                              <div>
                                <div class="fw-semibold">{{ role.label }}</div>
                                <div class="text-muted small">{{ role.description }}</div>
                                <div *ngIf="role.value === MembershipRoles.Owner && selectedOrgHasOwner" class="text-warning-emphasis small mt-1">
                                  <i class="bi bi-exclamation-triangle-fill me-1"></i>
                                  This organization already has an owner.
                                  Selecting this role will override the current owner.
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ==================== ORGANIZATION SECTION ==================== -->
              <div class="border-bottom pb-2 mb-3">
                <h6 class="fw-semibold mb-1">
                  <i class="bi bi-building me-2"></i>
                  Organization
                </h6>
                <p class="text-muted small mb-0">Create a new organization or add to an existing one</p>
              </div>

              <!-- Organization Mode Toggle -->
              <div class="btn-group w-100 mb-3" role="group" aria-label="Organization mode">
                <input
                  type="radio"
                  class="btn-check"
                  name="orgMode"
                  id="orgModeNew"
                  autocomplete="off"
                  [checked]="organizationMode === 'new'"
                  (change)="setOrganizationMode('new')"
                />
                <label class="btn btn-outline-primary" for="orgModeNew">
                  <i class="bi bi-building-add me-2"></i>
                  Create New Organization
                </label>

                <input
                  type="radio"
                  class="btn-check"
                  name="orgMode"
                  id="orgModeExisting"
                  autocomplete="off"
                  [checked]="organizationMode === 'existing'"
                  (change)="setOrganizationMode('existing')"
                />
                <label class="btn btn-outline-primary" for="orgModeExisting">
                  <i class="bi bi-building me-2"></i>
                  Add to Existing Organization
                </label>
              </div>

              <!-- New Organization Fields -->
              <div *ngIf="organizationMode === 'new'" class="card border-0 mb-4">
                <div class="card-body">
                  <div class="row">
                    <div class="col-12 mb-3">
                      <label for="organizationName" class="form-label">
                        Organization Name <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        id="organizationName"
                        formControlName="organizationName"
                        placeholder="Acme Drone Services"
                      />
                    </div>

                    <!-- Organization Type -->
                    <div class="col-md-4 mb-3">
                      <label for="organizationType" class="form-label">Organization Type <span class="text-danger">*</span></label>
                      <select
                        class="form-select"
                        id="organizationType"
                        formControlName="organizationType"
                        [class.is-invalid]="createForm.get('organizationType')?.invalid && createForm.get('organizationType')?.touched"
                      >
                        <option *ngFor="let type of organizationTypes" [value]="type.value">
                          {{ type.label }}
                        </option>
                      </select>
                    </div>

                    <!-- EIN (for business entities) -->
                    <div class="col-md-6 mb-3" *ngIf="showEinField">
                      <label for="ein" class="form-label">
                        EIN (Employer Identification Number)
                        <span class="text-danger" *ngIf="einRequired">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        id="ein"
                        formControlName="ein"
                        placeholder="XX-XXXXXXX"
                        [class.is-invalid]="createForm.get('ein')?.invalid && createForm.get('ein')?.touched"
                      />
                      <div *ngIf="createForm.get('ein')?.invalid && createForm.get('ein')?.touched" class="invalid-feedback">
                        <span *ngIf="createForm.get('ein')?.errors?.['required']">EIN is required for this organization type</span>
                        <span *ngIf="createForm.get('ein')?.errors?.['pattern']">Please enter a valid EIN (XX-XXXXXXX)</span>
                      </div>
                      <div class="form-text">9-digit number assigned by the IRS</div>
                    </div>

                    <!-- SSN (for individuals) -->
                    <div class="col-md-6 mb-3" *ngIf="showSsnField">
                      <label for="ssn" class="form-label">
                        SSN (Social Security Number)
                        <span class="text-danger" *ngIf="ssnRequired">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        id="ssn"
                        formControlName="ssn"
                        placeholder="XXX-XX-XXXX"
                        [class.is-invalid]="createForm.get('ssn')?.invalid && createForm.get('ssn')?.touched"
                      />
                      <div *ngIf="createForm.get('ssn')?.invalid && createForm.get('ssn')?.touched" class="invalid-feedback">
                        <span *ngIf="createForm.get('ssn')?.errors?.['required']">SSN is required for this organization type</span>
                        <span *ngIf="createForm.get('ssn')?.errors?.['pattern']">Please enter a valid SSN (XXX-XX-XXXX)</span>
                      </div>
                      <div class="form-text">9-digit number for individual tax identification</div>
                    </div>

                    <div class="col-12 mb-3">
                      <label for="streetAddress" class="form-label">
                        Street Address <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        id="streetAddress"
                        formControlName="streetAddress"
                        placeholder="123 Main Street"
                      />
                    </div>

                    <div class="col-md-5 mb-3">
                      <label for="city" class="form-label">
                        City <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        id="city"
                        formControlName="city"
                        placeholder="Springfield"
                      />
                    </div>

                    <div class="col-md-4 mb-3">
                      <label for="state" class="form-label">
                        State <span class="text-danger">*</span>
                      </label>
                      <app-us-state-select
                        inputId="state"
                        placeholder="Select state..."
                        formControlName="state"
                      ></app-us-state-select>
                    </div>

                    <div class="col-md-3 mb-3">
                      <label for="zipCode" class="form-label">
                        Zip Code <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        id="zipCode"
                        formControlName="zipCode"
                        placeholder="12345"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Existing Organization Selection -->
              <div *ngIf="organizationMode === 'existing'" class="card border-0 mb-4">
                <div class="card-body">
                  <div class="row">
                    <div class="col-12 mb-0">
                      <label for="organizationId" class="form-label">
                        Select Organization <span class="text-danger">*</span>
                      </label>
                      <select
                        class="form-select"
                        id="organizationId"
                        formControlName="organizationId"
                        [disabled]="loadingOrganizations"
                      >
                        <option value="">Select an organization...</option>
                        <option *ngFor="let org of organizations" [value]="org.id">
                          {{ org.name }}
                        </option>
                      </select>
                      <div *ngIf="loadingOrganizations" class="form-text">
                        <span class="spinner-border spinner-border-sm me-2"></span>
                        Loading organizations...
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ==================== INFO NOTICE ==================== -->
              <div class="alert alert-info mb-4">
                <p class="mb-2">
                  <i class="bi bi-info-circle-fill me-2"></i>
                  <strong>Important: Invitation email will NOT be sent automatically</strong>
                </p>
                <ul class="mb-0 small">
                  <li>The user profile and organization will be created immediately</li>
                  <li>An invitation will be created but the email will NOT be sent</li>
                  <li>You can send the invitation email later from the organization members page</li>
                  <li>This gives you time to complete additional setup before giving the user access</li>
                </ul>
              </div>

              <!-- ==================== ACTIONS ==================== -->
              <div class="d-flex gap-2">
                <button
                  type="submit"
                  class="btn btn-primary"
                  [disabled]="loading"
                >
                  <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!loading" class="bi bi-plus-circle me-2"></i>
                  {{ loading ? 'Creating...' : 'Create User & Organization' }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  (click)="cancel()"
                  [disabled]="loading"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
